import Box from "@storeblocks/box";
import InlineAlert from "@storeblocks/inline-alert";
import Modal from "@storeblocks/modal";
import { WrapLgMarginBottom } from "App/global-style";
import { NumericInput } from "Components/Form/Input";
import { InputMode } from "Components/Form/Input/inputTypes";
import { RadioButton } from "Components/Form/RadioButton";
import { VALUE_TYPE } from "Components/NumberValue";
import {
  MAX_DAILY_PAYOUT,
  useCalculateRecommendedValues,
} from "Components/SopCalculator/hooks";
import { SOPAgreementType } from "Components/SopCalculator/interfaces";
import { useTexts } from "Components/TextContext";
import { ThemedButton } from "Components/Themes/components/ThemedButton";
import { SopSicknessLeaveProductIntroFormValues } from "Features/CheckPrice/Products/LifeProducts/SopSicknessLeave/ProductIntro/sopSicknessLeaveProductIntroInterfaces";
import { formatToLocaleString } from "Formatting/formatNumber";
import { isDefined } from "Utils/functions";
import { useFormikContext } from "formik";
import React, { useCallback, useState } from "react";
import {
  StyledSurface,
  SurfaceHeading,
  SurfaceParagraph,
} from "./sopCalculatorStyled";

export const SopCalculator = (): React.ReactElement => {
  const [open, setOpen] = useState(false);
  const { getText } = useTexts();

  const { values, setFieldValue } =
    useFormikContext<SopSicknessLeaveProductIntroFormValues>();
  const { minValue, maxValue, recommendedDailyPayout } =
    useCalculateRecommendedValues(values.agreementType, values.revenue);

  const toggle = useCallback(() => {
    setOpen(!open);
  }, [open, setOpen]);

  const handleSubmit = (): void => {
    setFieldValue("dailyPayoutAmount", recommendedDailyPayout);
    setFieldValue("recommendedDailyMin", minValue);
    setFieldValue("recommendedDailyMax", maxValue);
    toggle();
  };

  return (
    <WrapLgMarginBottom>
      <Modal
        open={open}
        heading={getText("sopsicknessleaveform.calculator.heading")}
        trigger={
          <ThemedButton
            trackId="sop-calculator-modal"
            onClick={() => toggle()}
            variant="outlined"
          >
            {getText("sopsicknessleaveform.calculator.modalopenbutton")}
          </ThemedButton>
        }
        onClose={() => toggle()}
        content={
          <>
            <RadioButton
              id="agreementType"
              name="agreementType"
              label={getText(
                "sopsicknessleaveform.calculator.label.agreementtype",
              )}
              direction={"vertical"}
              options={[
                {
                  id: "agreementType-general_practitioner-option",
                  label: getText(
                    "sopsicknessleaveform.calculator.agreementtype.generalpracticioner",
                  ),
                  trackid: "avtaleform-fastlege",
                  value: SOPAgreementType.GENERAL_PRACTITIONER,
                },
                {
                  id: "agreementType-specialist-option",
                  label: getText(
                    "sopsicknessleaveform.calculator.agreementtype.specialistwithoperatinglicense",
                  ),
                  trackid: "agreementType-specialist-option",
                  value: SOPAgreementType.SPECIALIST_WITH_OPERATING_AGREEMENT,
                },
                {
                  id: "agreementType-withoutAgreement-option",
                  label: getText(
                    "sopsicknessleaveform.calculator.agreementtype.withoutagreement",
                  ),
                  trackid: "agreementType-withoutAgreement-option",
                  value: SOPAgreementType.WITHOUT_AGREEMENT,
                },
                {
                  id: "agreementType-substitute-option",
                  label: getText(
                    "sopsicknessleaveform.calculator.agreementtype.substitute",
                  ),
                  trackid: "agreementType-substitute-option",
                  value: SOPAgreementType.SUBSTITUTE,
                },
              ]}
            />
            <NumericInput
              name="revenue"
              id="revenue"
              label={getText("sopsicknessleaveform.label.grosspracticeincome")}
              inputMode={InputMode.NUMERIC}
              inputProps={{ autoComplete: "off", maxLength: 8 }}
              suffix={VALUE_TYPE.NOK}
              large
            />
            <Box display="flex">
              {values.agreementType &&
                values.agreementType !== SOPAgreementType.SUBSTITUTE &&
                isDefined(recommendedDailyPayout) && (
                  <StyledSurface>
                    <SurfaceHeading>
                      {getText("sopsicknessleaveform.calculator.surfacelabel", {
                        variables: {
                          recommendedDailyPayout: `${formatToLocaleString(
                            recommendedDailyPayout,
                          )} `,
                        },
                      })}
                    </SurfaceHeading>
                    <SurfaceParagraph>
                      {
                        "Veiledende dagpengebeløp beregnes på følgende måte: Brutto praksisinntekt/365"
                      }
                    </SurfaceParagraph>
                  </StyledSurface>
                )}
            </Box>
            {recommendedDailyPayout === MAX_DAILY_PAYOUT && (
              <InlineAlert
                title={
                  "Storebrand tilbyr ikke mer enn maksimalt 3 300 kr per dag"
                }
              />
            )}
          </>
        }
        footer={
          <>
            <ThemedButton
              trackId={"sopcalculator-submit-button"}
              type={"button"}
              onClick={handleSubmit}
              style={{ marginRight: "8px" }}
            >
              {getText("sopsicknessleaveform.calculator.submitbutton")}
            </ThemedButton>
            <ThemedButton
              trackId="sopcalculator-cancel-button"
              type="button"
              variant={"outlined"}
              onClick={toggle}
            >
              {getText("sopsicknessleaveform.calculator.cancelmodal")}
            </ThemedButton>
          </>
        }
      />
    </WrapLgMarginBottom>
  );
};
